import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Instagram, Star, Users, Sparkles } from 'lucide-react';

export default function StorePage() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen pt-20 md:pt-24"
    >
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[#1A231C] to-[#2C5530]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-white/70">Nuestra Tienda</span>
              <h1 className="font-cabinet font-bold text-3xl sm:text-4xl lg:text-5xl mt-3 mb-6">
                Visita Nuestra<br />Sala de Venta
              </h1>
              <p className="text-white/80 text-lg mb-8">
                Te invitamos a vivir la experiencia Gama Foods en nuestra sala de venta,
                donde encontrarás todos nuestros productos frescos en un ambiente cálido y acogedor.
                Nuestro equipo está listo para asesorarte.
              </p>
              
              {/* Features */}
              <div className="space-y-4 mb-8">
                {[
                  { icon: Star, title: 'Productos Premium', desc: 'Selección exclusiva de productos artesanales' },
                  { icon: Users, title: 'Atención Personalizada', desc: 'Te asesoramos para encontrar lo que buscas' },
                  { icon: Sparkles, title: 'Ambiente Acogedor', desc: 'Espacio diseñado para tu comodidad' },
                ].map((feature, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <feature.icon size={20} />
                    </div>
                    <div>
                      <strong className="block">{feature.title}</strong>
                      <span className="text-sm text-white/70">{feature.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <a
                  href="https://wa.me/56994798582?text=Hola!+Quiero+visitar+la+sala+de+venta"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-6 py-3 rounded-full hover:bg-[#1EBE5D] transition-colors"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Consultar Horarios
                </a>
                <a
                  href="https://www.instagram.com/gamafoodschiloe/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white/10 text-white font-medium px-6 py-3 rounded-full hover:bg-white/20 transition-colors"
                >
                  <Instagram size={18} />
                  Ver en Instagram
                </a>
              </div>
            </div>
            
            {/* Gallery */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <img
                  src="/images/foto1.jpeg"
                  alt="Sala de Venta"
                  className="rounded-2xl w-full h-64 object-cover"
                />
              </div>
              <img
                src="/images/foto2.jpeg"
                alt="Productos Frescos"
                className="rounded-xl h-40 object-cover w-full"
              />
              <img
                src="/images/foto4.jpeg"
                alt="Productos Frescos"
                className="rounded-xl h-40 object-cover w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Location */}
            <div className="bg-[#F3F3EF] p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#2C5530] text-white rounded-xl flex items-center justify-center">
                  <MapPin size={24} />
                </div>
                <h3 className="font-cabinet font-semibold text-xl">Ubicación</h3>
              </div>
              <address className="not-italic text-[#4A5D4E] mb-4">
                Pedro Aguirre Cerda #0194<br />
                Chonchi, Chiloé<br />
                Región de Los Lagos
              </address>
              <a
                href="https://maps.google.com/?q=Pedro+Aguirre+Cerda+0194+Chonchi"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#2C5530] font-medium hover:underline"
              >
                <MapPin size={16} />
                Ver en Google Maps
              </a>
            </div>

            {/* Hours */}
            <div className="bg-[#F3F3EF] p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#2C5530] text-white rounded-xl flex items-center justify-center">
                  <Clock size={24} />
                </div>
                <h3 className="font-cabinet font-semibold text-xl">Horarios</h3>
              </div>
              <div className="space-y-2 text-[#4A5D4E]">
                <div className="flex justify-between">
                  <span>Lunes a Sábado</span>
                  <strong>8:00 - 20:00</strong>
                </div>
                <div className="flex justify-between">
                  <span>Domingo</span>
                  <strong>9:00 - 19:00</strong>
                </div>
                <div className="flex justify-between pt-2 border-t border-[#E8EAE6] mt-2">
                  <span>🥖 Pan Fresco</span>
                  <strong className="text-[#E2B35B]">Desde 8:00</strong>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-[#2C5530] p-8 rounded-2xl text-white">
              <h3 className="font-cabinet font-semibold text-xl mb-4">¿Tienes alguna pregunta?</h3>
              <p className="text-white/80 mb-6">
                Contáctanos por WhatsApp para consultar disponibilidad, hacer pedidos o resolver cualquier duda.
              </p>
              <a
                href="https://wa.me/56994798582?text=Hola!+Tengo+una+consulta"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-5 py-3 rounded-full hover:bg-[#1EBE5D] transition-colors w-full justify-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Escribir por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* More Photos */}
      <section className="py-16 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-cabinet font-bold text-2xl sm:text-3xl text-[#1A231C]">Más de Nuestra Tienda</h2>
            <p className="text-[#808E83] mt-2">Síguenos en Instagram para ver más fotos y novedades</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['/images/foto5.jpeg', '/images/foto6.jpeg', '/images/foto7.jpeg', '/images/foto8.jpeg'].map((img, i) => (
              <a
                key={i}
                href="https://www.instagram.com/gamafoodschiloe/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative group overflow-hidden rounded-xl aspect-square"
              >
                <img src={img} alt={`Foto ${i + 1}`} className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Instagram className="text-white" size={32} />
                </div>
              </a>
            ))}
          </div>

          <div className="text-center mt-8">
            <a
              href="https://www.instagram.com/gamafoodschiloe/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#2C5530] font-medium hover:underline"
            >
              <Instagram size={20} />
              @gamafoodschiloe
            </a>
          </div>
        </div>
      </section>
    </motion.div>
  );
}
